import { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { Container, Button, Alert, Spinner, Card, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { reportService, ReportFilters, ReportStatistics, ReportSortOrder } from '../services/report.service';
import { format } from 'date-fns';
import { isJapaneseUiLanguage } from '../i18n/config';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { useAdminStorageViewAll } from '../hooks/useAdminStorageViewAll';
import { useMdUp } from '../hooks/useLaboSukeMdUp';
import { useListFirstDetailedFilterOnMount, usePersistedBoolean } from '../hooks/usePersistedBoolean';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import SampleManagerNavigation from '../components/samples/SampleManagerNavigation';
import { SampleManagerPageHeading } from '../components/samples/SampleManagerPageHeading';
import SampleListChromeTools, {
  ToolsMenuColVis,
  ToolsMenuExport,
  ToolsMenuHelp,
  ToolsMenuItem,
} from '../components/samples/SampleListChromeTools';
import UsageLedgerPdfCoverSettingsModal from '../components/samples/UsageLedgerPdfCoverSettingsModal';
import AdminStorageViewToggle from '../components/samples/AdminStorageViewToggle';
import ReportDetailedFilterFields from '../components/samples/ReportDetailedFilterFields';
import SampleNeedsStorageSetupAlert from '../components/samples/SampleNeedsStorageSetupAlert';
import { useSampleStorageSetupGate } from '../hooks/useSampleStorageSetupGate';
import BaseDateField, { BASE_DATE_FIELD_COMPACT_WIDTH } from '../components/samples/BaseDateField';
import KeywordDetailedFilterBar from '../components/samples/KeywordDetailedFilterBar';
import { navigateToSampleDetail } from '../utils/samplePageNavigation';
import { formatReportMobileFactsHtml } from '../utils/sampleLocationDisplay';
import { injectDataTableCellSeparators } from '../utils/injectDataTableCellSeparators';
import { destroyDataTableIfPresent } from '../utils/dataTableLifecycle';
import {
  buildDataTablesExportButtons,
  buildDataTablesLanguage,
  getKensukeJQuery,
  syncDataTablesPaginateVisibility,
} from '../utils/jqueryDatatablesShim';
import SampleListStatsCards, {
  type SampleListStatId,
} from '../components/samples/SampleListStatsCards';
import { deliverBlobToUser } from '../utils/downloadBlob';
import { getAxiosOrBlobErrorMessage } from '../utils/error.utils';
import { joinLabeledValue } from '../utils/joinLabeledValue';
import { resolveUsageLedgerPeriodLabels } from '../utils/usageLedgerPeriod';
import {
  USAGE_LEDGER_INVENTORY_VIEW,
} from '../constants/usageLedgerInventory';
import { buildReportHierarchyFromVisibleColumns } from '../utils/reportHierarchy';
import {
  REPORTS_DETAILED_FILTER_STORAGE_KEY,
  countDetailedReportFilters,
  normalizeReportFilters,
} from '../utils/reportFilters';
import {
  type ReportStockFilter,
  statFromStockFilter,
  stockFilterFromStat,
} from '../utils/reportStockFilter';
import { formatSampleQuantityWithUnit } from '../utils/sampleUnitLabels';
import { formatPathogenGenusDisplay } from '../utils/pathogenGenusDisplay';
import { escapeHtml } from '../utils/escapeHtml';
import {
  formatEntityDisplayName,
  softBreakIdentifier,
} from '../utils/softBreakIdentifier';
import { formatSampleTypeDisplay } from '../utils/sampleTypeLabels';
import {
  dataTablesLengthA11yLabels,
  dockReportsExportButtons,
  syncDataTablesLengthA11y,
} from '../utils/sampleListExportDock';
import { remPx, syncWeightedTableColgroup } from '../utils/syncDataTableColgroup';
import styles from './Reports.module.css';
// Note: DataTables CSS is loaded from local files in index.html (/vendor/datatables/css/)

/** 残数量など固定列を px 固定し、余りをサンプル名へ（% 指定が効かない対策） */
function syncReportsColgroup(table: HTMLTableElement | null): void {
  syncWeightedTableColgroup(table, (th) => {
    /* 短見出し「残」前提。旧 8.25rem は狭幅で名前を押し潰していた */
    if (th.classList.contains('report-qty-col')) return { role: 'fixed', widthPx: remPx(4.75) };
    if (th.classList.contains('report-creator-col')) return { role: 'fixed', widthPx: remPx(7.5) };
    if (th.classList.contains('report-meta-col')) return { role: 'fixed', widthPx: remPx(5.5) };
    if (th.classList.contains('report-drawer-col')) return { role: 'fixed', widthPx: remPx(4.5) };
    if (th.classList.contains('report-case-col')) return { role: 'fixed', widthPx: remPx(5.5) };
    if (th.classList.contains('report-type-col')) return { role: 'fixed', widthPx: remPx(5.5) };
    if (th.classList.contains('report-name-col')) return { role: 'fluid', weight: 0.62 };
    if (th.classList.contains('report-storage-col')) return { role: 'fluid', weight: 0.38 };
    return { role: 'fixed', widthPx: remPx(5) };
  });
}

function formatDrawerNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return String(value);
}

/** 数量など: モバイルカード用に短いラベルを付与（デスクトップは CSS でラベル非表示） */
function formatReportLabeledMeta(label: string, value: string): string {
  if (!value || value === '-') return '-';
  return `<span class="report-meta-with-label"><span class="report-meta-label">${escapeHtml(label)}</span><span class="visually-hidden" aria-hidden="true"> · </span> <span class="report-meta-value">${escapeHtml(value)}</span></span>`;
}


type ReportHierarchyBits = {
  storageRoom?: string | null;
  caseName?: string | null;
  drawerNumber?: number | null;
  type?: string | null;
  sampleName?: string | null;
};

export default function Reports() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [baseDate, setBaseDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [filters, setFilters] = useState<ReportFilters>({});
  const [keywordInput, setKeywordInput] = useState('');
  const [error, setError] = useState<string>('');
  const [showPdfCoverSettings, setShowPdfCoverSettings] = useState(false);
  /** 既定は残>0。残0はストリップで示唆し、クリックで表示 */
  const [stockFilter, setStockFilter] = useState<ReportStockFilter>('positive');
  const tableRef = useRef<HTMLTableElement>(null);
  const dataTableRef = useRef<any>(null);
  const baseDateRef = useRef(baseDate);
  const filtersRef = useRef<ReportFilters>({});
  const hierarchyRef = useRef<string[]>([]);
  const stockFilterRef = useRef<ReportStockFilter>(stockFilter);
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { showAdminViewToggle, adminViewAll, setAdminViewAll } = useAdminStorageViewAll(isAdmin);
  const storageSetupGate = useSampleStorageSetupGate(adminViewAll);
  /** md+: filter mid / 未満: 下帯。二重マウントしない */
  const isMdUp = useMdUp();
  const adminViewAllRef = useRef(adminViewAll);
  adminViewAllRef.current = adminViewAll;
  stockFilterRef.current = stockFilter;
  const [reportStats, setReportStats] = useState<ReportStatistics | null>(null);
  const [reportTotal, setReportTotal] = useState<number>(0);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
  const [isUsageLedgerPdfLoading, setIsUsageLedgerPdfLoading] = useState<boolean>(false);
  const [detailedFilterOpen, setDetailedFilterOpen, toggleDetailedFilter] = usePersistedBoolean(
    REPORTS_DETAILED_FILTER_STORAGE_KEY,
    false
  );

  // 列表示（colvis）に連動して API の hierarchy を更新する（Reports.tsx 内で sync）

  const syncReportHierarchyFromTable = useCallback((dt: { column: (idx: number) => { visible: () => boolean } }) => {
    hierarchyRef.current = buildReportHierarchyFromVisibleColumns((idx) => dt.column(idx).visible());
  }, []);

  const normalizedFilters = useMemo(() => normalizeReportFilters(filters), [filters]);
  const detailedFilterCount = useMemo(() => countDetailedReportFilters(filters), [filters]);
  useListFirstDetailedFilterOnMount(
    setDetailedFilterOpen,
    detailedFilterCount,
    REPORTS_DETAILED_FILTER_STORAGE_KEY
  );

  useEffect(() => {
    baseDateRef.current = baseDate;
  }, [baseDate]);

  useEffect(() => {
    filtersRef.current = normalizedFilters;
  }, [normalizedFilters]);

  // stats 到着や再描画で slot が載せ替わっても export / length 配置を戻す
  useLayoutEffect(() => {
    dockReportsExportButtons(dataTablesLengthA11yLabels(t));
    syncReportsColgroup(tableRef.current);
  }, [reportStats, i18n.language, reportTotal, t]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let timer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        dockReportsExportButtons(dataTablesLengthA11yLabels(t));
        /* columns.adjust の後に colgroup 同期（adjust が幅を壊すため） */
        dataTableRef.current?.columns.adjust();
        syncReportsColgroup(tableRef.current);
      }, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(timer);
    };
  }, [t]);

  /** クリア等で filters.keyword が変わったとき入力欄を同期 */
  useEffect(() => {
    setKeywordInput(filters.keyword || '');
  }, [filters.keyword]);

  /** キーワードはデバウンスしてから filters に反映（1打鍵ごとの ajax.reload を防ぐ） */
  const debouncedKeyword = useDebouncedValue(keywordInput, 300);
  useEffect(() => {
    const next = debouncedKeyword.trim() || undefined;
    setFilters((prev) => {
      const prevKw = prev.keyword || undefined;
      if (prevKw === next) return prev;
      return { ...prev, keyword: next };
    });
  }, [debouncedKeyword]);

  const destroyTable = useCallback(() => {
    destroyDataTableIfPresent(tableRef.current);
    dataTableRef.current = null;
  }, []);

  // DataTables初期化（マウント／言語変更時。タブ復帰は SPA 再マウントで十分なためフルリロードしない）
  useLayoutEffect(() => {
    if (!tableRef.current) {
      if (dataTableRef.current) {
        destroyTable();
      }
      return;
    }

    // DOMの準備を待ってから初期化（React Strict Modeの二重マウント対策）
    let colvisReloadTimer: number | undefined;
    const timerId = window.setTimeout(() => {
      if (!tableRef.current) return;

    destroyTable();

    try {
      if (typeof window === 'undefined' || !(window as any).jQuery || !(window as any).jQuery.fn.DataTable) {
        setError(t('common.datatables_not_loaded'));
        return;
      }

      const $ = (window as any).jQuery;

      destroyDataTableIfPresent(tableRef.current);

      $(tableRef.current).off('column-visibility.dt');
      window.clearTimeout(colvisReloadTimer);

      // 初期列表示（0–11, 13 表示、12 notes 非表示）に合わせて集計軸を設定
      hierarchyRef.current = buildReportHierarchyFromVisibleColumns((idx) => {
        if (idx === 12) return false;
        return idx >= 0 && idx <= 13;
      });

      dataTableRef.current = $(tableRef.current).DataTable({
        deferRender: false,
        /* 自前オーバーレイと二重になる DT 標準 processing は出さない */
        processing: false,
        serverSide: true,
        ajax: async (dtParams: any, callback: (data: any) => void) => {
          try {
            setIsTableLoading(true);
            if (dataTableRef.current) {
              syncReportHierarchyFromTable(dataTableRef.current);
            }
            const length = dtParams.length || 25;
            const start = dtParams.start || 0;
            const page = Math.floor(start / length) + 1;
            const rawOrder = dtParams.order;
            let sortOrders: ReportSortOrder[] | undefined;
            if (Array.isArray(rawOrder) && rawOrder.length > 0) {
              const parsed = rawOrder
                .map((o: { column?: number; dir?: string }) => ({
                  column: typeof o.column === 'number' ? o.column : parseInt(String(o.column ?? ''), 10),
                  dir:
                    String(o.dir ?? 'asc').toLowerCase() === 'desc'
                      ? ('desc' as const)
                      : ('asc' as const),
                }))
                .filter(
                  (o: ReportSortOrder) =>
                    Number.isInteger(o.column) && o.column >= 0 && o.column <= 13
                );
              if (parsed.length > 0) sortOrders = parsed;
            }
            const response = await reportService.getReportData({
              baseDate: baseDateRef.current,
              filters: filtersRef.current,
              hierarchy: hierarchyRef.current,
              view: USAGE_LEDGER_INVENTORY_VIEW,
              stockFilter: stockFilterRef.current,
              page,
              limit: length,
              sortOrders,
              adminViewAll: adminViewAllRef.current || undefined,
            });
            setReportStats(response.statistics || null);
            setReportTotal(response.pagination?.total || 0);
            setError('');
            callback({
              data: response.data || [],
              recordsTotal: response.pagination?.total || 0,
              recordsFiltered: response.pagination?.total || 0,
            });
          } catch (fetchError) {
            console.error('[Reports] DataTables fetch error:', fetchError);
            setError(t('reports.load_failed'));
            callback({ data: [], recordsTotal: 0, recordsFiltered: 0 });
          } finally {
            setIsTableLoading(false);
          }
        },
        columns: [
          /*
           * 既定表示は在庫の主軸のみ（保管庫〜残数量）。
           * 作製者・場所・BSL・病原体は ColVis で復元（1024px で横スクロールにしない）。
           */
          {
            data: 'hierarchy',
            title: `<span title="${escapeHtml(
              t('reports.creator_title', {
                defaultValue: t('sample.maker_name', { defaultValue: '作製者名' }),
              })
            )}">${escapeHtml(
              t('reports.creator', { defaultValue: t('sample.creator', { defaultValue: '作製者' }) })
            )}</span>`,
            render: (data: any) => {
              const name = data?.creator || '-';
              if (name === '-') return '-';
              const escaped = escapeHtml(String(name));
              return `<span class="report-creator-cell" title="${escaped}">${escaped}</span>`;
            },
            defaultContent: '-',
            className: 'report-hide-mobile report-meta-col report-creator-col',
            visible: Boolean(isAdmin && adminViewAll),
          },
          {
            data: 'hierarchy',
            title: t('reports.location'),
            render: (data: any) => data?.storageRoomLocation || '-',
            defaultContent: '-',
            className: 'report-hide-mobile report-meta-col',
            visible: false,
          },
          {
            data: 'hierarchy',
            title: t('reports.storage_room'),
            render: (data: any) => {
              const raw = data?.storageRoom;
              if (raw == null || raw === '') return '-';
              return escapeHtml(softBreakIdentifier(String(raw), t));
            },
            defaultContent: '-',
            className: 'report-storage-col',
            visible: true,
          },
          {
            data: 'hierarchy',
            title: t('reports.drawer'),
            render: (data: any) => formatDrawerNumber(data?.drawerNumber),
            defaultContent: '-',
            className: 'text-center report-drawer-col',
            visible: true,
          },
          {
            data: 'hierarchy',
            title: t('reports.case'),
            render: (data: any) =>
              data?.caseName ? escapeHtml(softBreakIdentifier(String(data.caseName), t)) : '-',
            defaultContent: '-',
            className: 'report-case-col',
            visible: true,
          },
          {
            data: 'hierarchy',
            title: `<span title="${escapeHtml(t('reports.bsl_level'))}">${escapeHtml(
              t('sample.bsl_level_short', { defaultValue: 'BSL' })
            )}</span>`,
            render: (data: any) => data?.bslLevel || '-',
            defaultContent: '-',
            className: 'report-hide-mobile report-meta-col',
            visible: false,
          },
          {
            data: 'hierarchy',
            title: t('reports.pathogen_genus'),
            render: (data: { pathogenGenus?: string | null } | null) => {
              const genus = formatPathogenGenusDisplay(data?.pathogenGenus);
              return genus || '-';
            },
            defaultContent: '-',
            className: 'report-hide-mobile report-meta-col',
            visible: false,
          },
          {
            data: 'hierarchy',
            title: t('reports.pathogen_species'),
            render: (data: { pathogenSpecies?: string | null } | null) =>
              data?.pathogenSpecies?.trim() || '-',
            defaultContent: '-',
            className: 'report-hide-mobile report-meta-col',
            visible: false,
          },
          {
            data: 'hierarchy',
            title: t('reports.pathogen_subspecies'),
            render: (data: { pathogenSubspecies?: string | null } | null) =>
              data?.pathogenSubspecies?.trim() || '-',
            defaultContent: '-',
            className: 'report-hide-mobile report-meta-col',
            visible: false,
          },
          {
            data: 'hierarchy',
            title: t('reports.pathogen_strain'),
            render: (data: { pathogenStrain?: string | null } | null) =>
              data?.pathogenStrain?.trim() || '-',
            defaultContent: '-',
            className: 'report-hide-mobile report-meta-col',
            visible: false,
          },
          {
            data: 'hierarchy',
            title: t('sample.sample_type'),
            render: (data: any) => {
              const raw = data?.type;
              if (raw == null || raw === '') return '-';
              return escapeHtml(formatSampleTypeDisplay(String(raw), t));
            },
            defaultContent: '-',
            className: 'report-type-col',
            visible: true,
          },
          {
            data: 'hierarchy',
            title: t('sample.sample_name'),
            render: (data: any, _type: any, row: any) => {
              const hierarchy = (data ?? row?.hierarchy) as ReportHierarchyBits | undefined;
              const value = hierarchy?.sampleName ?? row?.name;
              if (value === null || value === undefined || value === '') {
                return '-';
              }
              const rawName = String(value);
              const displayName = formatEntityDisplayName(rawName, t);
              const escapedRaw = escapeHtml(rawName);
              const escapedDisplay = escapeHtml(displayName);
              const softLabel = escapeHtml(softBreakIdentifier(rawName, t));
              const sampleId = Number(row?.id);
              const canOpenDetail = Number.isInteger(sampleId) && sampleId >= 1;
              const actionTitle = escapeHtml(
                joinLabeledValue(
                  canOpenDetail
                    ? t('sample.open_detail_page')
                    : t('reports.apply_name_filter', { defaultValue: 'Filter by this name' }),
                  displayName
                )
              );
              const typeDisp = formatSampleTypeDisplay(hierarchy?.type ?? row?.hierarchy?.type, t);
              const factsHtml = formatReportMobileFactsHtml(
                escapeHtml,
                hierarchy ?? row?.hierarchy,
                typeDisp
              );
              const idAttr = canOpenDetail ? ` data-id="${sampleId}"` : '';
              // 可視＝名前。aria も名前（操作説明は title）
              return `<div class="report-name-stack"><button type="button" class="report-name-open"${idAttr} data-name="${escapedRaw}" title="${actionTitle}" aria-label="${escapedDisplay}"><span class="report-name-text sample-ellipsis-prefer-end">${softLabel}</span>${factsHtml}</button></div>`;
            },
            defaultContent: '-',
            className: 'report-name-col',
            visible: true,
          },
          {
            data: 'notes',
            title: t('sample.notes'),
            defaultContent: '-',
            className: 'report-hide-mobile report-meta-col text-wrap-cell',
            visible: false,
            createdHeader: (th: HTMLElement) => {
              th.classList.add('text-wrap-cell');
            },
            render: (data: any) => {
              if (!data || data === null || data === undefined || data === '') {
                return '-';
              }
              return data;
            },
          },
          {
            data: 'remainingQuantity',
            title: `<span title="${escapeHtml(t('reports.remaining_quantity'))}">${escapeHtml(
              t('sample.remaining_quantity_short', { defaultValue: '残' })
            )}</span>`,
            className: 'text-end noVis report-qty-col',
            defaultContent: '0',
            render: (data: any, _type: string, row: { unit?: string | null }) => {
              const qty =
                data !== null && data !== undefined ? Number(data) : 0;
              const value = formatSampleQuantityWithUnit(qty, row?.unit, t);
              return formatReportLabeledMeta(
                t('sample.remaining_quantity_short', { defaultValue: t('reports.remaining_quantity') }),
                value
              );
            },
          },
        ],
        language: buildDataTablesLanguage(t),
        // 「全 _TOTAL_ 件」は在庫ストリップ（可/切れ）と誤解されるため自前件数帯を使う
        info: false,
        pageLength: 25,
        lengthMenu: [10, 25, 50, 100],
        ordering: true,
        orderMulti: true,
        // 既定表示列（保管庫→ケース→タイプ→名→残）でソート。列ヘッダ変更は reportSort へ
        order: [
          [2, 'asc'],
          [3, 'asc'],
          [4, 'asc'],
          [10, 'asc'],
          [11, 'asc'],
          [13, 'desc'],
        ],
        dom: 'lBrtp',
        searching: false,
        buttons: buildDataTablesExportButtons({
          t,
          asCollection: true,
          colvisOutsideCollection: true,
          colvisColumns: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
          filename: () => `${t('reports.list_title')}_${format(new Date(), 'yyyy-MM-dd')}`,
          excelTitle: t('reports.list_title'),
        }),
        responsive: false,
        autoWidth: false,
        createdRow: (row: HTMLElement) => {
          injectDataTableCellSeparators(row);
        },
        drawCallback: function (this: {
          api: () => Parameters<typeof syncDataTablesPaginateVisibility>[0];
        }) {
          syncDataTablesPaginateVisibility(this.api());
        },
        initComplete: function (this: {
          api: () => Parameters<typeof syncDataTablesPaginateVisibility>[0];
        }) {
          const api = this.api();
          syncDataTablesPaginateVisibility(api);
          if (dataTableRef.current) {
            syncReportHierarchyFromTable(dataTableRef.current);
          }
          dockReportsExportButtons(dataTablesLengthA11yLabels(t));
          syncReportsColgroup(tableRef.current);
          syncDataTablesLengthA11y('#reports-table-wrapper .dataTables_wrapper', {
            ...dataTablesLengthA11yLabels(t),
            lengthSlotSelector: '#reports-page .dataTables-length-slot',
          });
        },
      });

      $(tableRef.current)
        .off('click.reportNameOpen')
        .on('click.reportNameOpen', '.report-name-open', (e: { currentTarget: HTMLElement }) => {
          const id = parseInt(e.currentTarget.getAttribute('data-id') || '', 10);
          if (Number.isInteger(id) && id >= 1) {
            navigateToSampleDetail(navigate, id, {
              preferAdminViewAll: adminViewAllRef.current,
              returnPath: '/reports',
            });
            return;
          }
          const name = e.currentTarget.getAttribute('data-name')?.trim();
          if (name) setKeywordInput(name);
        });

      $(tableRef.current).on('column-visibility.dt', () => {
        if (!dataTableRef.current) return;
        syncReportHierarchyFromTable(dataTableRef.current);
        // 列増減後に DT の幅計算 → 続けて px 固定（adjust 単独だと名前が潰れる）
        dataTableRef.current.columns.adjust();
        syncReportsColgroup(tableRef.current);
        window.clearTimeout(colvisReloadTimer);
        colvisReloadTimer = window.setTimeout(() => {
          dataTableRef.current?.ajax.reload(null, false);
          requestAnimationFrame(() => syncReportsColgroup(tableRef.current));
        }, 280);
      });
    } catch (err) {
      console.error('DataTables初期化エラー:', err);
      setError(t('common.table_init_failed'));
    }
    }, 100);

    return () => {
      window.clearTimeout(timerId);
      window.clearTimeout(colvisReloadTimer);
      const $ = getKensukeJQuery();
      if ($ && tableRef.current) {
        $(tableRef.current).off('click.reportNameOpen');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, t]);

  useEffect(() => {
    if (!dataTableRef.current) return;
    const showCreator = Boolean(isAdmin && adminViewAll);
    const creatorCol = dataTableRef.current.column(0);
    if (creatorCol.visible() !== showCreator) {
      creatorCol.visible(showCreator, false);
      syncReportHierarchyFromTable(dataTableRef.current);
      dataTableRef.current.columns.adjust();
    }
    dataTableRef.current.ajax.reload(null, true);
  }, [baseDate, normalizedFilters, adminViewAll, isAdmin, stockFilter, syncReportHierarchyFromTable]);

  const onReportStatSelect = useCallback((id: SampleListStatId) => {
    if (id === 'disposed') return;
    setStockFilter((prev) => {
      const next = stockFilterFromStat(id);
      // 同じカード再クリックで利用可（既定）に戻す
      if (statFromStockFilter(prev) === id) return 'positive';
      return next;
    });
  }, []);

  const renderReportStatsCards = () =>
    reportStats ? (
      <SampleListStatsCards
        stats={{
          total: reportStats.totalSamples,
          available: reportStats.availableSamples ?? 0,
          outOfStock: reportStats.outOfStockSamples ?? 0,
        }}
        unfiltered
        isAdmin={isAdmin}
        showDescriptions={false}
        compact
        activeStat={statFromStockFilter(stockFilter)}
        onStatSelect={onReportStatSelect}
        titleOverrides={{
          total: t('reports.stock_filter_total_title', {
            defaultValue: 'すべて表示 · 基準日時点の全件',
          }),
          available: t('reports.stock_filter_available_title', {
            defaultValue: '利用可のみ表示 · 既定',
          }),
          outOfStock: t('reports.stock_filter_out_of_stock_title', {
            defaultValue: '残なしのみ表示',
          }),
        }}
      />
    ) : null;

  const tableCountLabel =
    stockFilter === 'zero'
      ? t('reports.table_count_out_of_stock', { defaultValue: '残なし' })
      : stockFilter === 'all'
        ? t('reports.total_count')
        : t('reports.table_count_available', { defaultValue: '利用可' });

  const handleUsageLedgerPdfExport = async () => {
    setIsUsageLedgerPdfLoading(true);
    setError('');
    try {
      const blob = await reportService.exportUsageLedgerPdf({
        baseDate,
        filters: normalizedFilters,
        adminViewAll: adminViewAll || undefined,
      });
      const mime = (blob.type || '').toLowerCase();
      if (mime.includes('json') || mime.includes('text/html')) {
        const text = await blob.text();
        let message = t('reports.usage_ledger_export_failed', {
          defaultValue: t('reports.export_failed'),
        });
        try {
          const json = JSON.parse(text) as { error?: { message?: string }; message?: string };
          const msg = json.error?.message || json.message;
          if (typeof msg === 'string' && msg.trim()) message = msg;
        } catch {
          /* keep fallback */
        }
        setError(message);
        return;
      }
      deliverBlobToUser(blob, `usage_ledger_${baseDate}.pdf`);
    } catch (err: unknown) {
      setError(
        await getAxiosOrBlobErrorMessage(
          err,
          t('reports.usage_ledger_export_failed', {
            defaultValue: t('reports.export_failed'),
          })
        )
      );
    } finally {
      setIsUsageLedgerPdfLoading(false);
    }
  };

  const pdfFullLabel = t('reports.usage_ledger_pdf');
  const pdfHelpTitle = joinLabeledValue(
    t('reports.usage_ledger_pdf_help'),
    t('reports.base_date_change_note')
  );

  const { periodStartLabel: ledgerPeriodStartLabel, periodEndLabel: ledgerPeriodEndLabel } =
    useMemo(() => resolveUsageLedgerPeriodLabels(baseDate), [baseDate]);

  /** 特定病原体の年次保管義務注記は日本法・日本語 UI のみ */
  const showJapanUsageLedgerObligationNote = isJapaneseUiLanguage(
    i18n.resolvedLanguage || i18n.language
  );

  /** 年次保管用の主操作。chrome に埋めて到達不能にしない */
  const renderUsageLedgerPdfButton = () => (
    <Button
      variant="primary"
      size="sm"
      className="reports-usage-ledger-pdf-btn flex-shrink-0"
      disabled={isUsageLedgerPdfLoading}
      title={pdfHelpTitle}
      aria-label={pdfFullLabel}
      onClick={() => {
        void handleUsageLedgerPdfExport();
      }}
    >
      {isUsageLedgerPdfLoading ? (
        <>
          <Spinner animation="border" size="sm" className="me-1" />
          <span>
            {t('reports.usage_ledger_exporting', { defaultValue: '出力中…' })}
          </span>
        </>
      ) : (
        <>
          <i className="fas fa-file-pdf me-1" aria-hidden="true" />
          <span>{pdfFullLabel}</span>
        </>
      )}
    </Button>
  );

  return (
    <Container fluid className="mt-2 px-3" id="reports-page">
      <SampleManagerNavigation />
      {/* ナビ短名「帳票」と可視 h1 が縦を奪うため sr-only。管理者トグルは md+ ここ / xs は統計帯へ */}
      <SampleManagerPageHeading
        shortLabel={t('navigation.reports_short', { defaultValue: 'Reports' })}
        fullLabel={t('reports.title')}
        iconClassName="fas fa-chart-bar"
        srOnly
      />
      <p className="visually-hidden">{t('reports.page_role')}</p>
      {storageSetupGate.isReady && !storageSetupGate.hasStorageRooms ? (
        <SampleNeedsStorageSetupAlert />
      ) : null}
      {showAdminViewToggle && isMdUp ? (
        <div className="d-flex justify-content-end mb-2">
          <AdminStorageViewToggle
            checked={adminViewAll}
            onChange={setAdminViewAll}
            inputId="reports-admin-view-all"
            dense
            className="flex-shrink-0"
            labelKey="sample.admin_view_all_label"
            shortLabelKey="sample.admin_view_all_label_short"
            offShortLabelKey="sample.admin_view_scope_mine_short"
            hintKey="sample.admin_view_all_hint"
          />
        </div>
      ) : null}
      {!showAdminViewToggle ? (
        <Alert variant="info" className="mb-2 py-2 small">
          {t('reports.user_only_note')}
        </Alert>
      ) : null}

      {/* 年次使用簿（基準日＋過去1年）。義務注記は日本語 UI のみ（日本法） */}
      <div className="reports-usage-ledger-band mb-2" role="region" aria-label={pdfFullLabel}>
        <div className="reports-usage-ledger-band__main">
          <BaseDateField
            id="reports-base-date"
            label={t('reports.base_date')}
            value={baseDate}
            max={format(new Date(), 'yyyy-MM-dd')}
            lang={i18n.language}
            title={t('reports.open_calendar')}
            width={BASE_DATE_FIELD_COMPACT_WIDTH}
            onChange={setBaseDate}
            className="reports-base-date"
          />
          <div className="reports-usage-ledger-band__period small">
            <span className="text-muted d-block">
              {t('reports.usage_ledger_period_label', { defaultValue: '集計期間' })}
            </span>
            <strong className="reports-usage-ledger-band__period-value">
              {t('reports.usage_ledger_period_value', {
                start: ledgerPeriodStartLabel,
                end: ledgerPeriodEndLabel,
                defaultValue: '{{start}} 〜 {{end}}（過去1年）',
              })}
            </strong>
          </div>
          {renderUsageLedgerPdfButton()}
        </div>
        {showJapanUsageLedgerObligationNote ? (
          <p className="reports-usage-ledger-band__note small text-muted mb-0">
            {t('reports.usage_ledger_obligation_note')}
          </p>
        ) : null}
      </div>

      <KeywordDetailedFilterBar
        variant="plain"
        keywordId="reports-filter-keyword"
        keyword={keywordInput}
        onKeywordChange={setKeywordInput}
        keywordPlaceholder={t('reports.filter_keyword_placeholder_short', {
          defaultValue: t('reports.filter_keyword_placeholder'),
        })}
        keywordAriaLabel={t('reports.filter_keyword_aria', {
          defaultValue: '帳票検索',
        })}
        keywordTitle={t('reports.filter_keyword_help', {
          defaultValue: '名前・タイプで絞り込み · スペース区切りで複数可',
        })}
        hideKeywordLabel
        detailedOpen={detailedFilterOpen}
        detailedCount={detailedFilterCount}
        onToggleDetailed={toggleDetailedFilter}
        onClear={() => {
          setKeywordInput('');
          setFilters({});
          setStockFilter('positive');
        }}
        mid={
          reportStats && isMdUp ? (
            <div className="d-flex align-items-center gap-2 flex-shrink-0 keyword-detailed-filter-mid">
              {renderReportStatsCards()}
            </div>
          ) : undefined
        }
        trailing={
          <>
            <SampleListChromeTools
              toggleId="reports-export-toggle"
              toggleClassName="reports-export-toggle"
              busy={isUsageLedgerPdfLoading}
              compactChrome
              help={
                <CollapsibleHelpSection
                  compact
                  expandedPortalId="reports-help-expanded"
                  titleKey="reports.how_to_use_title"
                  descriptionKey="reports.how_to_use_desc"
                  stepsKey={[
                    'reports.how_to_use_step1',
                    'reports.how_to_use_step2',
                    'reports.how_to_use_step3',
                    'reports.how_to_use_step4',
                  ]}
                  storageKey="reports"
                />
              }
              menu={
                <>
                  <ToolsMenuHelp pageSelector="#reports-page" />
                  <ToolsMenuItem
                    iconClassName="fas fa-file-pdf"
                    label={pdfFullLabel}
                    title={pdfHelpTitle}
                    onClick={() => {
                      void handleUsageLedgerPdfExport();
                    }}
                    disabled={isUsageLedgerPdfLoading}
                  />
                  <ToolsMenuItem
                    iconClassName="fas fa-file-alt"
                    label={t('reports.pdf_cover_settings_title', {
                      defaultValue: '使用簿PDFの表紙設定',
                    })}
                    onClick={() => setShowPdfCoverSettings(true)}
                  />
                  <Dropdown.Divider />
                  <ToolsMenuColVis pageSelector="#reports-page" />
                  <ToolsMenuExport
                    pageSelector="#reports-page"
                    iconClassName="fas fa-file-excel text-success"
                  />
                </>
              }
            />
            <div className="dataTables-length-slot reports-length-chrome flex-shrink-0" aria-hidden="true" />
          </>
        }
        detailedPanel={
          <ReportDetailedFilterFields
            idPrefix="reports"
            filters={filters}
            onFiltersChange={setFilters}
            isAdmin={isAdmin}
            showCreatorFilter={adminViewAll}
            showPathogenFields
            size="sm"
          />
        }
      />
      <div id="reports-help-expanded" />
      {/* 狭幅: 在庫ストリップを filter 帯の下へ（xl 専用 mid だと残0示唆が消える） */}
      {!isMdUp && reportStats ? (
        <div className="mb-1 sample-list-mobile-stats reports-mobile-stats">
          {renderReportStatsCards()}
          {showAdminViewToggle ? (
            <AdminStorageViewToggle
              checked={adminViewAll}
              onChange={setAdminViewAll}
              inputId="reports-admin-view-all-mobile"
              compact
              className="sample-list-mobile-admin"
              labelKey="sample.admin_view_all_label"
              shortLabelKey="sample.admin_view_all_label_short"
              offShortLabelKey="sample.admin_view_scope_mine_short"
              hintKey="sample.admin_view_all_hint"
            />
          ) : null}
        </div>
      ) : null}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
          <div className="mt-2">
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => {
                setError('');
                dataTableRef.current?.ajax.reload(null, false);
              }}
            >
              {t('common.retry')}
            </Button>
          </div>
        </Alert>
      )}

      {/* ページ h1「レポート」と「一覧」見出しが二重になるためパネル見出しは置かない（件数は DT info / モバイル帯） */}
      <Card className="border-0 shadow-sm mb-2 sample-panel-card">
        {/*
          読込オーバーレイは DataTables が包む table の兄弟にしない。
          wrapper 内で出し入れすると DT 移動後の insertBefore で白画面になる。
        */}
        <Card.Body className="p-0 position-relative">
          {isTableLoading ? (
            <div className={styles.tableLoadingOverlay} aria-live="polite" role="status">
              <Spinner animation="border" size="sm" className="flex-shrink-0" />
              <span className="small text-muted">{t('common.loading')}</span>
            </div>
          ) : null}
          <div
            id="reports-table-wrapper"
            className={`table-responsive ${styles.tableScroll}`}
          >
            <table
              ref={tableRef}
              className="table table-striped table-hover table-bordered mb-0"
              style={{ width: '100%' }}
              aria-busy={isTableLoading || undefined}
            >
              <thead></thead>
              <tbody></tbody>
            </table>
          </div>
          {/* DT info は出さない。在庫ストリップと同じラベルで件数を明示（全幅） */}
          {!isTableLoading ? (
            <div
              className="px-3 py-2 border-top small text-muted"
              aria-live="polite"
            >
              {joinLabeledValue(
                tableCountLabel,
                `${reportTotal}${t('common.unit_items')}`
              )}
            </div>
          ) : null}
        </Card.Body>
      </Card>
      <UsageLedgerPdfCoverSettingsModal
        show={showPdfCoverSettings}
        onHide={() => setShowPdfCoverSettings(false)}
      />
    </Container>
  );
}

